import * as React from 'react'
import {type ValidSystemTheme} from 'notability-services-common-universal'

import {FontContext, FontTheme} from '../fonts'
import {PartialRecord, ResolutionBreak} from '../shared'
import {Theme} from './theme'
import {clientThemeContext, serverThemeContext} from './context'

import {StringRecord} from 'notability-services-common-universal'
import {StyleValues} from './types'
import {useResolutionBreak} from '../hooks'

type ThemeFn<A> = (theme: Theme, font: FontTheme) => A

/**
 * Gets the current theme from the react component context and passes it into the styling func `input`.
 *
 * **NOTE: This can only be used within the context of a component that is a child of the themeContext.Provider.**
 *
 * @param input - a function that takes a theme and returns a map of styles
 *
 * Example usage:
 * ```
 * function MyComponent({foo}: MyComponentArgs) {
 *   const s = useThemedStyles(theme => {
 *     // A themed style object
 *     myStyles: {
 *       color: theme.core.f1,
 *       backgroundColor: theme.core.b1
 *     },
 *     // A function that takes a param and returns a style object based on the param
 *     myStyles: (foo: boolean) => ({
 *       color: foo ? theme.core.f1 : theme.accent.base,
 *       backgroundColor: theme.core.b1
 *     })
 *   })
 *   return (
 *      <>
 *      <div styles={s.myStyles}/>
 *      <div styles={s.myConditionalStyles(foo)}/>
 *      </>
 *   )
 * }
 * ```
 */
export function useThemedStyles<A extends StringRecord<StyleValues>>(input: ThemeFn<A>): A {
  // Grab the theme from react context.
  const {theme, font} = useTheme()
  return input(theme.theme, font)
}

export function useThemedStylesCustom<A>(input: ThemeFn<StringRecord<A>>): StringRecord<A> {
  // Grab the theme from react context.
  const {theme, font} = useTheme()
  return input(theme.theme, font)
}

/**
 * Enforces type checking of a styling function.
 * Can be used with `useThemedStyles` to declare styles outside of a component definition.
 * @param input A function that takes a theme and returns a map of styles.
 * @returns `input`, but type-checked in a useful way.
 *
 * Example usage:
 * ```
 * const styles = defineStyles(theme => {
 *   // A themed style object
 *   myStyles: {
 *     color: theme.core.f1,
 *     backgroundColor: theme.core.b1
 *   },
 *   // A function that takes a param and returns a style object based on the param
 *   myStyles: (foo: boolean) => ({
 *     color: foo ? theme.core.f1 : theme.accent.base,
 *     backgroundColor: theme.core.b1
 *   })
 * })
 *
 * function MyComponent({foo}: MyComponentArgs) {
 *   const s = useThemedStyles(styles)
 *   return (
 *      <>
 *      <div styles={s.myStyles}/>
 *      <div styles={s.myConditionalStyles(foo)}/>
 *      </>
 *   )
 * }
 * ```
 *
 */
export function defineStyles<A extends StringRecord<StyleValues>>(input: ThemeFn<A>): ThemeFn<A> {
  return input
}

export function defineStylesCustom<A>(input: ThemeFn<StringRecord<A>>): ThemeFn<StringRecord<A>> {
  return input
}

export type BreakStyle = StyleValues & PartialRecord<ResolutionBreak | 'desktop', StyleValues>

/**
 * Works the same as defineStyles, but will merge down sub-styles that share a name with the current
 * resolution break.
 *
 * const styles = defineStyles(theme => {
 *   // A themed style object
 *   myStyles: {
 *     marginLeft: '24px',
 *     color: theme.core.f1,
 *     backgroundColor: theme.core.b1,
 *
 *     // Will be applied over the above at resolution break 'mobile'
 *     mobile: {
 *       marginLeft: '12px'
 *     }
 *   }
 * })
 *
 * @param input Function that returns object with styles applied.
 * @returns Function to be passed to themedStylesBreaks
 */
export function defineStylesWithBreaks<A extends StringRecord<BreakStyle>>(input: ThemeFn<A>): ThemeFn<A> {
  return input
}

/**
 * Apply the current theme and breaks to produce a set of CSS properties.
 *
 * @param input The return value from defineBreakStyles
 * @returns
 */
export function useThemedStylesWithBreaks<A extends StringRecord<BreakStyle>>(
  input: ThemeFn<A>,
  defaultBreak?: ResolutionBreak
): A {
  const {theme, font} = useTheme()
  const themed = input(theme.theme, font)
  const keys = Object.keys(themed)

  // If no resolution break was added, add the hook to get it.
  const resBreak = useResolutionBreak(defaultBreak)

  /**
   * Take the base object of each set, then apply the resolution break on top of it.
   */
  return keys.reduce((acc, key) => {
    // Destructure out the resolution-specific values from the entry, so they can be applied directly as a style.
    const {mobile, md, lg, xl, xxl, ...sharedValues} = themed[key]!
    const breakStyle = themed[key] as BreakStyle
    const breakValues = breakStyle[resBreak] || {}
    const desktopValues = resBreak === 'mobile' ? {} : breakStyle['desktop'] || {}
    ;(acc as any)[key] = {
      ...sharedValues,
      ...breakValues,
      ...desktopValues
    }
    return acc
  }, {} as A)
}

export const useTheme = () => {
  const {nativeTheme, nativeThemeName} = React.useContext(serverThemeContext)
  const {themeValues, ...otherThemeContext} = React.useContext(clientThemeContext)
  const font = React.useContext(FontContext)

  return {
    font,
    theme: (nativeTheme
      ? {
          ...themeValues,
          theme: nativeTheme,
          name: nativeThemeName
        }
      : themeValues) as {
      theme: Theme
      name: ValidSystemTheme | 'custom'
      userSetting: ValidSystemTheme | 'custom'
    },
    ...otherThemeContext
  }
}
